// @givmo/board — the Schedule O draft (CBG-YR5-SCHO; the derivation §4, frame p8; the backend's
// `board-schedule-o.md` §5 and §W1).
//
// One card per entry the read serves, line 8b before line 11b: Schedule O's own label, "Copy", an IRS
// sentence quoted with its source where one is served, the sentences Board built from the year's
// records, and "From your records:" with links to those records. The text is the read's, shown exactly
// as "Copy" copies it; nothing here composes, edits or proposes a sentence. With no sentence, one line
// in Board's voice says what Board holds, beside the instructions' own sentence, quoted — never offered
// as the organization's answer. Reached from line 11b and from line 8b's no-committee note on the Form
// 990 page, with that page's address (`form990-address.ts`).

import { Fragment, type ReactNode } from 'react';
import { Button, Card, CardBody, Skeleton, toast } from '@givmo/design-system';
import { useBoardHost } from '../../board-host';
import { Link, useSearch } from '../../board-routing';
import { BOARD_PAGES } from '../../board-routes';
import { PageShell } from '../../components/page-shell';
import { RegisteredDisclaimer } from '../../components/registered-disclaimer';
import { displayDate } from '../../lib/display';
import { addressOf, searchOf } from './form990-address';
import { classSurfaces, monthDayWords, type Form990Quotation } from './form990-decode';
import { LINK, QUIET, Quotation } from './form990-evidence';
import type { Form990Address } from './form990-queries';
import { form990Words as fw } from './form990-words';
import {
  line11bState,
  type ScheduleODraft,
  type ScheduleOLine11b,
  type ScheduleOLine8b,
} from './schedule-o-decode';
import { useScheduleODraft } from './schedule-o-queries';
import { scheduleOWords as w } from './schedule-o-words';

const SECONDARY = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const TEXT = 'font-sans text-s16 font-w400 leading-h15 tracking-givmo text-text-strong';

export function ScheduleOPage() {
  const { orgRef, isReady } = useBoardHost();
  const search = useSearch();
  const address = addressOf(search);
  const query = useScheduleODraft(orgRef, address, isReady);
  const read = query.data;
  const draft = read?.kind === 'page' ? read.draft : null;

  return (
    <PageShell
      title={w.pageTitle}
      description={
        draft?.tax_year !== null && draft?.tax_year !== undefined
          ? w.lead(draft.tax_year.named_year)
          : undefined
      }
      testId="page-schedule-o"
    >
      {!isReady || orgRef === null || query.isPending ? (
        <Loading />
      ) : query.isError || read === undefined ? (
        <ReadFailed onRetry={() => void query.refetch()} />
      ) : read.kind === 'not-served' ? (
        <QuietCard testId="schedule-o-not-served">{w.notServed}</QuietCard>
      ) : read.kind === 'type-refused' ? (
        <QuietCard testId="schedule-o-type-refused">{read.sentence ?? fw.typeRefused}</QuietCard>
      ) : read.kind !== 'page' ? (
        // A stale year or a day the read can't use: the Form 990 page is where a year is chosen.
        <QuietCard testId="schedule-o-refused">
          <Link
            to={BOARD_PAGES.form990}
            search={read.kind === 'tax-year-unknown' ? chosenDay(address) : undefined}
            exact
            className={LINK}
            data-testid="schedule-o-refused-link"
          >
            {w.chooseYear}
          </Link>
        </QuietCard>
      ) : read.draft.tax_year === null ? (
        <QuietCard testId="schedule-o-no-year">
          <Link
            to={BOARD_PAGES.form990}
            exact
            className={LINK}
            data-testid="schedule-o-no-year-link"
          >
            {w.noTaxYear}
          </Link>
        </QuietCard>
      ) : (
        <TheDraft draft={read.draft} address={address} orgRef={orgRef} />
      )}
    </PageShell>
  );
}

/** The month and day chosen with no year end recorded, together or not at all, without the year: a
 *  stale year leaves them standing, so the Form 990 page doesn't ask for them again. */
function chosenDay(address: Form990Address): Record<string, number> | undefined {
  return address.yearEndMonth !== null && address.yearEndDay !== null
    ? { year_end_month: address.yearEndMonth, year_end_day: address.yearEndDay }
    : undefined;
}

function Loading() {
  return (
    <div className="flex flex-col gap-24" data-testid="schedule-o-loading">
      <Skeleton className="h-[220px] w-full rounded-xl" />
      <Skeleton className="h-[220px] w-full rounded-xl" />
    </div>
  );
}

function QuietCard({ children, testId }: { children: ReactNode; testId: string }) {
  return (
    <Card testId={testId}>
      <CardBody>
        <p className={SECONDARY}>{children}</p>
      </CardBody>
    </Card>
  );
}

function ReadFailed({ onRetry }: { onRetry: () => void }) {
  return (
    <Card testId="schedule-o-error">
      <CardBody className="flex flex-col items-start gap-12">
        <p className={SECONDARY}>{w.loadFailed}</p>
        <Button
          appearance="saas"
          tone="secondary"
          onClick={onRetry}
          testId="schedule-o-error-retry"
        >
          {fw.tryAgain}
        </Button>
      </CardBody>
    </Card>
  );
}

/** "April 8" — a meeting's day as its minutes' link names it (frame p8). */
function monthDay(iso: string | null): string {
  if (iso === null) return '';
  const [, month, day] = iso.split('-').map(Number);
  return monthDayWords(month ?? null, day ?? null);
}

function TheDraft({
  draft,
  address,
  orgRef,
}: {
  draft: ScheduleODraft;
  address: Form990Address;
  orgRef: string;
}) {
  if (draft.tax_year === null) return null;
  // The Form 990 page for this same year: its last day, and a chosen day as the address carries it.
  const year = { ...searchOf(address), tax_year_ends_on: draft.tax_year.ends_on };
  return (
    <>
      {draft.line_8b === null && draft.line_11b === null ? (
        <QuietCard testId="schedule-o-no-entry">{w.noEntry}</QuietCard>
      ) : null}
      {draft.line_8b !== null ? <Line8b line={draft.line_8b} year={year} /> : null}
      {draft.line_11b !== null ? <Line11b line={draft.line_11b} /> : null}

      {/* The consult-counsel and evidence slots (§6): the registry's words for each class the read
          names, only once a class is in force; until then nothing draws here at all. */}
      {classSurfaces(draft.disclaimer_class_ids).map((surface) => (
        <RegisteredDisclaimer
          key={surface}
          surface={surface}
          orgRef={orgRef}
          testId={`schedule-o-class-${surface}`}
        />
      ))}
    </>
  );
}

function Line8b({ line, year }: { line: ScheduleOLine8b; year: Record<string, string | number> }) {
  // Line 8a's tables on the Form 990 page hold the year's meetings and written consents. That page
  // brings line 8a into view itself, so the router leaves the scroll alone: its scroll to the top
  // lands after the page's when the page's read is cached.
  const at8a = { ...year, line: '8a' };
  const from = [
    line.meetings.length > 0 ? (
      <Link
        key="meetings"
        to={BOARD_PAGES.form990}
        search={at8a}
        resetScroll={false}
        className={LINK}
        data-testid="schedule-o-8b-meetings"
      >
        {w.theMeetings(line.meetings.length)}
      </Link>
    ) : null,
    line.written_consents.length > 0 ? (
      <Link
        key="consents"
        to={BOARD_PAGES.form990}
        search={at8a}
        resetScroll={false}
        className={LINK}
        data-testid="schedule-o-8b-consents"
      >
        {w.theConsents(line.written_consents.length)}
      </Link>
    ) : null,
  ].filter((link) => link !== null);
  return (
    <Entry
      label={line.label}
      quotation={line.quotation}
      text={line.text}
      state={w.noMeetingsOrConsents}
      from={from}
      testId="schedule-o-8b"
    />
  );
}

function Line11b({ line }: { line: ScheduleOLine11b }) {
  const copy = line.copy_provided;
  const review = line.review;
  const from = [
    copy !== null ? (
      <Link
        key="copy"
        to={BOARD_PAGES.returnCopy}
        params={{ recordId: copy.record_id }}
        className={LINK}
        data-testid="schedule-o-11b-copy-provided"
      >
        {w.theCopyProvided(displayDate(copy.provided_on))}
      </Link>
    ) : null,
    review?.kind === 'motion' && review.meeting_id !== null ? (
      review.minutes_record_id !== null ? (
        <Link
          key="review"
          to="/meetings/$meetingId/minutes"
          params={{ meetingId: review.meeting_id }}
          className={LINK}
          data-testid="schedule-o-11b-review"
        >
          {w.theMeetingsMinutes(monthDay(review.held_on))}
        </Link>
      ) : (
        // A meeting with no minutes yet is named, and opened, as the meeting.
        <Link
          key="review"
          to="/meetings/$meetingId"
          params={{ meetingId: review.meeting_id }}
          className={LINK}
          data-testid="schedule-o-11b-review"
        >
          {w.theMeeting(monthDay(review.held_on))}
        </Link>
      )
    ) : review?.kind === 'written_consent' && review.written_consent_id !== null ? (
      <Link
        key="review"
        to="/consents/$consentId"
        params={{ consentId: review.written_consent_id }}
        className={LINK}
        data-testid="schedule-o-11b-review"
      >
        {w.theConsentDated(review.consent_date === null ? null : displayDate(review.consent_date))}
      </Link>
    ) : null,
  ].filter((link) => link !== null);
  const state = line11bState(line);
  return (
    <Entry
      label={line.label}
      quotation={line.text === null ? line.quotation : null}
      text={line.text}
      state={
        state === 'no-copy'
          ? w.noCopy
          : state === 'every-copy-withdrawn'
            ? w.everyCopyWithdrawn
            : w.copyReachedNoOne
      }
      from={from}
      testId="schedule-o-11b"
    />
  );
}

/** One entry: its label and "Copy", the IRS's sentence quoted, the text (or Board's one line), and
 *  the records it was built from. */
function Entry({
  label,
  quotation,
  text,
  state,
  from,
  testId,
}: {
  label: string;
  quotation: Form990Quotation | null;
  text: string | null;
  state: string;
  from: readonly ReactNode[];
  testId: string;
}) {
  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(w.copied);
    } catch {
      // The text is on screen and selectable, so a refused clipboard is a missing convenience.
      toast.error(w.copyRefused);
    }
  }
  return (
    <Card testId={testId}>
      <CardBody className="flex flex-col gap-16">
        <div className="flex items-start justify-between gap-16">
          <h2
            className="pt-[7px] font-sans text-s13 font-w600 tracking-givmo text-text-strong"
            data-testid={`${testId}-label`}
          >
            {label}
          </h2>
          {text !== null ? (
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => void copy(text)}
              testId={`${testId}-copy`}
            >
              {w.copy}
            </Button>
          ) : null}
        </div>
        {quotation !== null ? (
          <Quotation
            paragraphs={[quotation.text]}
            citation={quotation.citation}
            testId={`${testId}-quotation`}
          />
        ) : null}
        {text !== null ? (
          <p className={`${TEXT} select-text`} data-testid={`${testId}-text`}>
            {text}
          </p>
        ) : (
          <p className={SECONDARY} data-testid={`${testId}-state`}>
            {state}
          </p>
        )}
        {from.length > 0 ? (
          <p className={QUIET} data-testid={`${testId}-from`}>
            {w.fromYourRecords}{' '}
            {from.map((link, index) => (
              <Fragment key={index}>
                {index > 0 ? ' · ' : null}
                {link}
              </Fragment>
            ))}
          </p>
        ) : null}
      </CardBody>
    </Card>
  );
}
